import React from 'react';
import { Grid, Typography } from '@mui/material';
import { InfoCard, SparkDataSearchBar } from 'sharedComponents/UI';

import TypeDetailsContainer from 'components/CreateSpark/Modals/SelectStepTypeModal/TypeDetailsContainer';
import StepSelectorItemSkeleton from 'sharedComponents/Skeleton/StepSelectorItemSkeleton';
import { getIsSearchableQuery } from 'utilities/index';
import { usePluginSearch } from 'hooks/SparkBuilder/pluginFunction/usePluginSearch';
import { useInfinitePlugins } from 'hooks/Plugins/useInfinitePlugins';
import type { Plugins } from 'types/Plugins';
import type { PluginAuthentication } from 'types/PluginAuthentications';

type Props = {
  selectPlugin: (
    selectedPlugin:
      | {
          plugin: Plugins | undefined;
          pluginConnections: number | undefined;
          pluginAuthentications: PluginAuthentication[] | undefined;
        }
      | undefined
  ) => void;
};
function SearchPluginModal({ selectPlugin }: Props) {
  const [query, setQuery] = React.useState('');
  const [searchedPlugins, setSearchedPlugins] = React.useState<Plugins[]>([]);
  const {
    plugins: pluginWithConnections,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfinitePlugins(query);

  const plugins = pluginWithConnections?.map(({ plugin }) => plugin);

  const { mutateAsync: searchPlugins, isLoading: isSearchingForPlugins } =
    usePluginSearch();

  const handleLatElementView = React.useCallback(() => {
    if (!hasNextPage || isLoading || isFetchingNextPage || !!query) {
      return;
    }

    fetchNextPage();
  }, [fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, query]);

  const handleChange = async (query: string) => {
    const isSearchableQuery = getIsSearchableQuery(query);
    setQuery(query);

    if (!isSearchableQuery) {
      setSearchedPlugins([]);
      return;
    }

    if (searchedPlugins.length) {
      setSearchedPlugins((prev) =>
        prev.filter((prevSpark) =>
          prevSpark.attributes.name?.toLowerCase().includes(query.toLowerCase())
        )
      );
      return;
    }

    const response = await searchPlugins({
      queryString: query,
    });
    const plugins = response.data;
    if (plugins && plugins?.length > 0) {
      setSearchedPlugins(plugins);
    }
  };

  const pluginsToRender = React.useMemo(() => {
    if (searchedPlugins.length) {
      return searchedPlugins;
    }

    return plugins;
  }, [plugins, searchedPlugins]);

  const onPluginClick = React.useCallback(
    (_selectedStepTypeID: string, resourceID?: string) => {
      const plugin = pluginWithConnections?.find(
        (plugin) => plugin.plugin.id === resourceID
      );
      selectPlugin(plugin);
    },
    [pluginWithConnections, selectPlugin]
  );

  const renderSearchResults = React.useMemo(() => {
    const isSearchableQuery = getIsSearchableQuery(query);

    if (!!query && !isSearchableQuery) {
      return (
        <Grid container>
          <InfoCard
            cardStyles={{ marginRight: '16px', width: '100%' }}
            info='Please enter at least 3 characters'
          />
        </Grid>
      );
    }

    if (isSearchableQuery && !searchedPlugins.length) {
      if (isSearchingForPlugins) {
        const NO_OF_ITEMS_ARRAY: number[] = [];
        for (let i = 1; i <= 6; i++) {
          NO_OF_ITEMS_ARRAY.push(i);
        }

        return (
          <Grid container>
            {NO_OF_ITEMS_ARRAY.map((value) => (
              <Grid item md={12} xl={6} padding={1}>
                <StepSelectorItemSkeleton />
              </Grid>
            ))}
          </Grid>
        );
      }
      return (
        <Grid container justifyContent='center' >
          <InfoCard
            cardStyles={{ marginRight: '16px', width: '100%' }}
            info='No plugins found'
            type='error'
          />
        </Grid>
      );
    }

    return (
      <TypeDetailsContainer
        onLastElementView={handleLatElementView}
        plugins={pluginsToRender}
        isLoading={isLoading || isSearchingForPlugins}
        onClick={onPluginClick}
        typeID={''}
        isFetchingNextPage={isFetchingNextPage}
        height='21rem'
      />
    );
  }, [
    handleLatElementView,
    isFetchingNextPage,
    isLoading,
    isSearchingForPlugins,
    onPluginClick,
    pluginsToRender,
    query,
    searchedPlugins.length,
  ]);

  return (
    <Grid container xs={12} minHeight='20rem'>
      <Grid item xs={12}>
        <Grid item xs={12}>
          <SparkDataSearchBar
            setQuery={handleChange}
            placeholder='Search Plugins'
          />
        </Grid>
        {renderSearchResults}
      </Grid>
    </Grid>
  );
}

export default SearchPluginModal;
